import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useMobileNav } from '../hooks/useMobileNav';
import {
  GlobalSearchBar,
  RevenueTab,
  AuditLogTab,
  EmailHealthTab,
  OrgBillingPanel,
  OrgUsagePanel,
  OrgQuickActionsBar,
} from './SuperAdminCommandCenter';

/**
 * SuperAdminDashboard
 * Platform-level cross-organization dashboard.
 * Roles: super_admin only.
 */
function SuperAdminDashboard({ user, onLogout }) {
  const mobileNav = useMobileNav();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [busyOrgId, setBusyOrgId] = useState(null);
  // Password reset state
  const [resetTarget, setResetTarget] = useState(null);  // user object being reset
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { email, temporary_password }

  // Platform Admins tab state
  const [superAdmins, setSuperAdmins] = useState([]);
  const [showAddSuperAdmin, setShowAddSuperAdmin] = useState(false);
  const [newSAForm, setNewSAForm] = useState({ email: '', full_name: '', phone: '', password: '' });
  const [creatingSA, setCreatingSA] = useState(false);
  const [createSAError, setCreateSAError] = useState('');
  const [busySAId, setBusySAId] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const s = await api.getSuperAdminStats();
        setStats(s);
      } else if (activeTab === 'organizations') {
        const orgs = await api.getSuperAdminOrganizations({
          search: search || undefined,
          is_active: statusFilter !== '' ? statusFilter : undefined,
        });
        setOrganizations(orgs);
      } else if (activeTab === 'platform-admins') {
        const sas = await api.listPlatformSuperAdmins();
        setSuperAdmins(sas);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    setCreateSAError('');
    if (newSAForm.password.length < 8) {
      setCreateSAError('Password must be at least 8 characters.');
      return;
    }
    setCreatingSA(true);
    try {
      await api.superAdminCreateUser({
        email: newSAForm.email.trim().toLowerCase(),
        password: newSAForm.password,
        full_name: newSAForm.full_name.trim(),
        phone: newSAForm.phone || null,
        role: 'super_admin',
      });
      setShowAddSuperAdmin(false);
      setNewSAForm({ email: '', full_name: '', phone: '', password: '' });
      // Refresh list
      const sas = await api.listPlatformSuperAdmins();
      setSuperAdmins(sas);
    } catch (err) {
      setCreateSAError(err.message || 'Failed to create super-admin');
    } finally {
      setCreatingSA(false);
    }
  };

  const handleToggleSAActive = async (sa) => {
    if (sa.is_self) return;
    setBusySAId(sa._id);
    try {
      if (sa.is_active) {
        if (!window.confirm(`Deactivate ${sa.email}? They will be unable to log in until re-activated.`)) {
          setBusySAId(null);
          return;
        }
        await api.superAdminDeactivateUser(sa._id);
      } else {
        await api.superAdminActivateUser(sa._id);
      }
      const sas = await api.listPlatformSuperAdmins();
      setSuperAdmins(sas);
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setBusySAId(null);
    }
  };

  const openOrgDetail = async (org) => {
    setSelectedOrg(org);
    setOrgDetail(null);
    try {
      const detail = await api.getSuperAdminOrganizationDetail(org._id);
      setOrgDetail(detail);
    } catch (err) {
      alert(`Failed to load detail: ${err.message}`);
      setSelectedOrg(null);
    }
  };

  const handleSuspend = async (orgId) => {
    if (!window.confirm('Suspend this organization? Their users will be unable to log in.')) return;
    setBusyOrgId(orgId);
    try {
      await api.superAdminSuspendOrganization(orgId);
      await loadData();
      if (selectedOrg && selectedOrg._id === orgId) {
        await openOrgDetail({ ...selectedOrg, is_active: false });
      }
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setBusyOrgId(null);
    }
  };

  const handleActivate = async (orgId) => {
    setBusyOrgId(orgId);
    try {
      await api.superAdminActivateOrganization(orgId);
      await loadData();
      if (selectedOrg && selectedOrg._id === orgId) {
        await openOrgDetail({ ...selectedOrg, is_active: true });
      }
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setBusyOrgId(null);
    }
  };

  const handleDelete = async (orgId) => {
    setBusyOrgId(orgId);
    try {
      const res = await api.superAdminDeleteOrganization(orgId);
      alert(`Organization deleted. ${Object.keys(res.deleted_counts || {}).length} collections purged.`);
      setShowDeleteConfirm(null);
      setSelectedOrg(null);
      setOrgDetail(null);
      await loadData();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setBusyOrgId(null);
    }
  };

  const handlePlanChange = async (orgId, newPlan) => {
    try {
      await api.superAdminUpdateOrganization(orgId, { 'subscription.plan': newPlan });
      await loadData();
      if (selectedOrg && selectedOrg._id === orgId) {
        await openOrgDetail(selectedOrg);
      }
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await api.superAdminResetUserPassword(resetTarget._id);
      setResetResult({
        email: res.email,
        temporary_password: res.temporary_password,
      });
      setResetTarget(null);
    } catch (err) {
      alert(`Failed to reset password: ${err.message}`);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="dashboard" data-theme="admin">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={mobileNav.toggle}
        aria-label="Toggle navigation"
        data-testid="mobile-nav-toggle"
      >
        {mobileNav.isOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-nav-backdrop ${mobileNav.isOpen ? 'show' : ''}`} onClick={mobileNav.close}></div>
      <div className={`sidebar ${mobileNav.isOpen ? 'mobile-open' : ''}`} onClick={(e) => {
        if (e.target.closest('.nav-item')) mobileNav.close();
      }}>
        <div className="sidebar-header">
          <h2>⚡ Platform Admin</h2>
          <p style={{ fontSize: '0.875rem', opacity: 0.85 }}>{user?.full_name || 'Super Admin'}</p>
          <span className="badge" style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))', fontSize: '0.7rem', marginTop: '0.25rem', display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
            SUPER ADMIN
          </span>
        </div>

        <ul className="nav-list">
          <li
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="nav-platform-overview"
          >
            🌐 Platform Overview
          </li>
          <li
            className={`nav-item ${activeTab === 'organizations' ? 'active' : ''}`}
            onClick={() => setActiveTab('organizations')}
            data-testid="nav-organizations"
          >
            🏢 Organizations
          </li>
          <li
            className={`nav-item ${activeTab === 'platform-admins' ? 'active' : ''}`}
            onClick={() => setActiveTab('platform-admins')}
            data-testid="nav-platform-admins"
          >
            👑 Platform Admins
          </li>
          <li
            className={`nav-item ${activeTab === 'migrations' ? 'active' : ''}`}
            onClick={() => setActiveTab('migrations')}
            data-testid="nav-migrations"
          >
            🗃️ DB Migrations
          </li>
          <li
            className={`nav-item ${activeTab === 'demo-requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('demo-requests')}
            data-testid="nav-demo-requests"
          >
            📅 Demo Requests
          </li>
          <li
            className={`nav-item ${activeTab === 'revenue' ? 'active' : ''}`}
            onClick={() => setActiveTab('revenue')}
            data-testid="nav-revenue"
          >
            📈 Revenue
          </li>
          <li
            className={`nav-item ${activeTab === 'audit-log' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit-log')}
            data-testid="nav-audit-log"
          >
            📜 Audit Log
          </li>
          <li
            className={`nav-item ${activeTab === 'email-health' ? 'active' : ''}`}
            onClick={() => setActiveTab('email-health')}
            data-testid="nav-email-health"
          >
            📧 Email Health
          </li>
        </ul>

        <button className="logout-btn" onClick={onLogout} data-testid="logout-btn">
          🚪 Logout
        </button>
      </div>

      {/* Main content */}
      <div className="main-content">
        {/* PLATFORM OVERVIEW */}
        {activeTab === 'overview' && (
          <>
            <div className="page-header">
              <h1>🌐 Platform Command Center</h1>
              <p>Cross-organization analytics and platform health</p>
            </div>

            {loading ? (
              <div>Loading platform stats...</div>
            ) : stats ? (
              <>
                {/* Top stats grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                  }}
                  data-testid="platform-stats-grid"
                >
                  <div className="stat-card" data-testid="stat-total-orgs">
                    <div className="stat-label">Total Organizations</div>
                    <div className="stat-value">{stats.total_organizations}</div>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                      {stats.active_organizations} active • {stats.suspended_organizations} suspended
                    </div>
                  </div>
                  <div className="stat-card" data-testid="stat-total-users">
                    <div className="stat-label">Total Users</div>
                    <div className="stat-value">{stats.total_users}</div>
                  </div>
                  <div className="stat-card" data-testid="stat-total-children">
                    <div className="stat-label">Total Children</div>
                    <div className="stat-value">{stats.total_children}</div>
                  </div>
                  <div className="stat-card" data-testid="stat-total-bookings">
                    <div className="stat-label">Total Bookings</div>
                    <div className="stat-value">{stats.total_bookings}</div>
                  </div>
                  <div className="stat-card" data-testid="stat-platform-revenue">
                    <div className="stat-label">Platform Revenue</div>
                    <div className="stat-value">£{(stats.total_revenue || 0).toFixed(2)}</div>
                  </div>
                </div>

                {/* Plan distribution */}
                <div className="card" style={{ marginTop: '2rem' }} data-testid="plan-distribution-card">
                  <h3 style={{ marginBottom: '1rem' }}>📦 Subscription Plan Distribution</h3>
                  {Object.keys(stats.plan_distribution || {}).length === 0 ? (
                    <div className="empty-state">No subscription data yet</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                      {Object.entries(stats.plan_distribution).map(([plan, count]) => (
                        <div
                          key={plan}
                          style={{
                            padding: '1rem',
                            background: 'hsl(var(--muted))',
                            borderRadius: 'var(--radius)',
                          }}
                          data-testid={`plan-card-${plan}`}
                        >
                          <div style={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600 }}>
                            {plan}
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
                            {count}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            organization{count === 1 ? '' : 's'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Breakdown card */}
                <div className="card" style={{ marginTop: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>👥 User Breakdown</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="stat-card">
                      <div className="stat-label">Parents</div>
                      <div className="stat-value">{stats.total_parents}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Staff (incl. drivers, admins)</div>
                      <div className="stat-value">{stats.total_staff}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">Failed to load platform stats</div>
            )}
          </>
        )}

        {/* ORGANIZATIONS LIST + DETAIL */}
        {activeTab === 'organizations' && (
          <>
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <h1>🏢 Organizations</h1>
                <p>Manage all tenants on the platform</p>
              </div>
              <GlobalSearchBar onOpenOrg={(o) => openOrgDetail(o)} />
            </div>

            {/* Filters */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 240 }}>
                  <label>Search by name, slug, or email</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadData()}
                    placeholder="Type to search..."
                    data-testid="org-search-input"
                  />
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: 180 }}>
                  <label>Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="org-status-filter"
                  >
                    <option value="">All</option>
                    <option value="true">Active only</option>
                    <option value="false">Suspended only</option>
                  </select>
                </div>
                <button className="btn btn-primary" onClick={loadData} data-testid="apply-filters-btn">
                  🔍 Apply
                </button>
              </div>
            </div>

            {loading ? (
              <div>Loading organizations...</div>
            ) : organizations.length === 0 ? (
              <div className="empty-state">No organizations found</div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }} data-testid="organizations-list">
                {organizations.map((org) => (
                  <div
                    key={org._id}
                    className="card"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}
                    data-testid={`org-card-${org._id}`}
                  >
                    <div style={{ flex: 1, minWidth: 280 }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                        <h3 style={{ margin: 0 }}>{org.name}</h3>
                        <span
                          className={`badge ${org.is_active === false ? 'badge-warning' : 'badge-success'}`}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {org.is_active === false ? 'SUSPENDED' : 'ACTIVE'}
                        </span>
                        <span className="badge" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--foreground))', fontSize: '0.7rem' }}>
                          {(org.subscription && org.subscription.plan) || 'unspecified'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                        <code>{org.slug}</code> • ID: <code>{org._id}</code>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                        ✉️ {org.contact_email || '—'} {org.contact_phone ? `• 📞 ${org.contact_phone}` : ''}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.875rem' }}>
                        <span>👥 <strong>{org.user_count}</strong> users</span>
                        <span>👶 <strong>{org.child_count}</strong> children</span>
                        <span>📅 <strong>{org.booking_count}</strong> bookings</span>
                        <span>🏬 <strong>{org.center_count}</strong> centers</span>
                        <span>👨‍🏫 <strong>{org.staff_count}</strong> staff</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 160 }}>
                      <button className="btn btn-primary" onClick={() => openOrgDetail(org)} data-testid={`view-org-${org._id}`}>
                        📂 View Detail
                      </button>
                      {org.is_active === false ? (
                        <button
                          className="btn btn-ghost"
                          onClick={() => handleActivate(org._id)}
                          disabled={busyOrgId === org._id}
                          data-testid={`activate-org-${org._id}`}
                        >
                          ✅ Activate
                        </button>
                      ) : (
                        <button
                          className="btn btn-ghost"
                          onClick={() => handleSuspend(org._id)}
                          disabled={busyOrgId === org._id || org.slug === 'wizkids-wiseapp'}
                          data-testid={`suspend-org-${org._id}`}
                        >
                          ⛔ Suspend
                        </button>
                      )}
                      {org.slug !== 'wizkids-wiseapp' && (
                        <button
                          className="btn btn-ghost"
                          style={{ color: 'hsl(0 70% 50%)' }}
                          onClick={() => setShowDeleteConfirm(org)}
                          disabled={busyOrgId === org._id}
                          data-testid={`delete-org-${org._id}`}
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Organization Detail Modal */}
      {selectedOrg && (
        <div className="modal-overlay" onClick={() => { setSelectedOrg(null); setOrgDetail(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800 }} data-testid="org-detail-modal">
            <div className="modal-header">
              <h2>🏢 {selectedOrg.name}</h2>
              <button className="close-btn" onClick={() => { setSelectedOrg(null); setOrgDetail(null); }} data-testid="close-org-detail">✕</button>
            </div>

            {!orgDetail ? (
              <div>Loading detail...</div>
            ) : (
              <>
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0 }}>Profile</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
                    <div><strong>Slug:</strong> <code>{orgDetail.organization.slug}</code></div>
                    <div><strong>Type:</strong> {orgDetail.organization.type}</div>
                    <div><strong>Email:</strong> {orgDetail.organization.contact_email || '—'}</div>
                    <div><strong>Phone:</strong> {orgDetail.organization.contact_phone || '—'}</div>
                    <div><strong>Status:</strong> {orgDetail.organization.is_active === false ? '⛔ Suspended' : '✅ Active'}</div>
                    <div>
                      <strong>Plan:</strong>
                      <select
                        value={(orgDetail.organization.subscription && orgDetail.organization.subscription.plan) || 'professional'}
                        onChange={(e) => handlePlanChange(orgDetail.organization._id, e.target.value)}
                        style={{ marginLeft: '0.5rem', padding: '0.25rem' }}
                        data-testid="change-plan-select"
                      >
                        <option value="starter">Starter</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Phase K: quick actions + billing detail + usage */}
                <OrgQuickActionsBar
                  orgId={orgDetail.organization._id}
                  ownerUser={(orgDetail.users || []).find((u) => u.role === 'org_owner')}
                  onDone={() => openOrgDetail(orgDetail.organization)}
                />
                <OrgBillingPanel orgId={orgDetail.organization._id} />
                <OrgUsagePanel orgId={orgDetail.organization._id} />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div className="stat-card">
                    <div className="stat-label">Children</div>
                    <div className="stat-value">{orgDetail.metrics.child_count}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Users</div>
                    <div className="stat-value">{orgDetail.metrics.user_count}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Bookings</div>
                    <div className="stat-value">{orgDetail.metrics.booking_count}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">This Month £</div>
                    <div className="stat-value">£{(orgDetail.metrics.month_revenue || 0).toFixed(0)}</div>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginTop: 0 }}>👥 Users ({orgDetail.users.length})</h3>
                  {orgDetail.users.length === 0 ? (
                    <div className="empty-state">No users in this org yet</div>
                  ) : (
                    <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>Role</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>Active</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orgDetail.users.map((u) => (
                            <tr key={u._id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`super-admin-user-row-${u._id}`}>
                              <td style={{ padding: '0.5rem' }}>{u.email}</td>
                              <td style={{ padding: '0.5rem' }}>{u.role}</td>
                              <td style={{ padding: '0.5rem' }}>{u.is_active ? '✅' : '⛔'}</td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => setResetTarget(u)}
                                  data-testid={`reset-password-btn-${u._id}`}
                                  title="Reset this user's password to a temporary value"
                                >
                                  🔑 Reset password
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* PLATFORM ADMINS TAB */}
      {activeTab === 'platform-admins' && (
        <>
          <div className="page-header">
            <h1>👑 Platform Admins</h1>
            <p>Accounts that can sign in with super-admin authority across every organization.</p>
          </div>

          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', maxWidth: 600 }}>
                Platform admins can read, edit and delete data in <strong>every</strong> organization on the
                platform. Add only people you fully trust. Each creation is recorded in the audit log.
              </div>
              <button
                className="btn btn-primary"
                onClick={() => { setCreateSAError(''); setShowAddSuperAdmin(true); }}
                data-testid="add-super-admin-btn"
              >
                ➕ Add super-admin
              </button>
            </div>
          </div>

          {loading ? (
            <div>Loading platform admins...</div>
          ) : superAdmins.length === 0 ? (
            <div className="empty-state">No platform admins found.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Phone</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {superAdmins.map((sa) => (
                    <tr key={sa._id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`platform-admin-row-${sa._id}`}>
                      <td style={{ padding: '0.75rem' }}>
                        {sa.email}{sa.is_self && (
                          <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem', padding: '0.1rem 0.4rem', borderRadius: 4, background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>YOU</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>{sa.full_name || '—'}</td>
                      <td style={{ padding: '0.75rem' }}>{sa.phone || '—'}</td>
                      <td style={{ padding: '0.75rem' }}>{sa.created_at ? String(sa.created_at).slice(0, 10) : '—'}</td>
                      <td style={{ padding: '0.75rem' }}>{sa.is_active ? '✅ Active' : '⛔ Disabled'}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', marginRight: '0.25rem' }}
                          onClick={() => setResetTarget(sa)}
                          disabled={sa.is_self}
                          title={sa.is_self ? 'Use the Change Password option for your own account.' : "Reset this admin's password"}
                          data-testid={`reset-password-btn-${sa._id}`}
                        >
                          🔑 Reset password
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => handleToggleSAActive(sa)}
                          disabled={sa.is_self || busySAId === sa._id}
                          title={sa.is_self ? 'You cannot disable your own account.' : (sa.is_active ? 'Disable this admin' : 'Re-enable this admin')}
                          data-testid={`toggle-active-btn-${sa._id}`}
                        >
                          {sa.is_active ? '⛔ Disable' : '✅ Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Add Super-Admin Modal */}
      {showAddSuperAdmin && (
        <div className="modal-overlay" onClick={() => !creatingSA && setShowAddSuperAdmin(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="add-super-admin-modal">
            <div className="modal-header">
              <h2>👑 Add platform super-admin</h2>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              This account will have full read/write/delete access to every organization on the platform.
              Only add people you fully trust.
            </p>
            <form onSubmit={handleCreateSuperAdmin} autoComplete="off">
              <div className="form-group">
                <label htmlFor="sa-email">Email</label>
                <input
                  id="sa-email"
                  type="email"
                  value={newSAForm.email}
                  onChange={(e) => setNewSAForm({ ...newSAForm, email: e.target.value })}
                  required
                  autoComplete="off"
                  data-testid="add-super-admin-email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="sa-name">Full name</label>
                <input
                  id="sa-name"
                  type="text"
                  value={newSAForm.full_name}
                  onChange={(e) => setNewSAForm({ ...newSAForm, full_name: e.target.value })}
                  required
                  data-testid="add-super-admin-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="sa-phone">Phone (optional)</label>
                <input
                  id="sa-phone"
                  type="tel"
                  value={newSAForm.phone}
                  onChange={(e) => setNewSAForm({ ...newSAForm, phone: e.target.value })}
                  data-testid="add-super-admin-phone"
                />
              </div>
              <div className="form-group">
                <label htmlFor="sa-pw">Initial password (min 8 chars)</label>
                <input
                  id="sa-pw"
                  type="password"
                  value={newSAForm.password}
                  onChange={(e) => setNewSAForm({ ...newSAForm, password: e.target.value })}
                  minLength={8}
                  required
                  autoComplete="new-password"
                  data-testid="add-super-admin-password"
                />
              </div>
              {createSAError && (
                <div
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'hsl(0 70% 95%)',
                    border: '1px solid hsl(0 70% 80%)',
                    borderRadius: '0.375rem',
                    color: 'hsl(0 70% 35%)',
                    fontSize: '0.875rem',
                    marginBottom: '0.75rem',
                  }}
                  data-testid="add-super-admin-error"
                >
                  {createSAError}
                </div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowAddSuperAdmin(false)}
                  disabled={creatingSA}
                  data-testid="add-super-admin-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creatingSA}
                  data-testid="add-super-admin-submit-btn"
                >
                  {creatingSA ? 'Creating…' : '👑 Create super-admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="delete-confirm-modal">
            <div className="modal-header">
              <h2>⚠️ Confirm Permanent Deletion</h2>
            </div>
            <p>
              You are about to permanently delete <strong>{showDeleteConfirm.name}</strong> and ALL of its data
              (children, bookings, users, custody events, etc.).
            </p>
            <p style={{ color: 'hsl(0 70% 50%)', fontWeight: 600 }}>This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(null)} data-testid="cancel-delete-btn">
                Cancel
              </button>
              <button
                className="btn"
                style={{ background: 'hsl(0 70% 50%)', color: 'white' }}
                disabled={busyOrgId === showDeleteConfirm._id}
                onClick={() => handleDelete(showDeleteConfirm._id)}
                data-testid="confirm-delete-btn"
              >
                🗑️ Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={() => !resetting && setResetTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="reset-password-confirm-modal">
            <div className="modal-header">
              <h2>🔑 Reset password</h2>
            </div>
            <p>
              Reset the password for <strong>{resetTarget.email}</strong>?
            </p>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
              A strong temporary password will be generated and shown on the next screen.
              You'll need to deliver it to the user securely (e.g. via SMS or in person).
              They will be prompted to change it on next sign-in.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setResetTarget(null)}
                disabled={resetting}
                data-testid="reset-password-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleResetPassword}
                disabled={resetting}
                data-testid="reset-password-confirm-btn"
              >
                {resetting ? 'Resetting…' : '🔑 Generate new password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Result Modal — shows the one-time temporary password */}
      {resetResult && (
        <div className="modal-overlay" onClick={() => setResetResult(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="reset-password-result-modal">
            <div className="modal-header">
              <h2>✅ Password reset</h2>
            </div>
            <p style={{ marginBottom: '0.5rem' }}>
              Temporary password for <strong>{resetResult.email}</strong>:
            </p>
            <div
              style={{
                padding: '0.75rem 1rem',
                background: 'hsl(var(--muted))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.5rem',
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
              data-testid="reset-password-result-value"
            >
              {resetResult.temporary_password}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.75rem' }}>
              ⚠️ This password will not be shown again. Copy it now and deliver it securely.
              The user must change it after their next login.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(resetResult.temporary_password);
                  } catch {/* clipboard may be unavailable in some browsers — selection still works */}
                }}
                data-testid="reset-password-result-copy-btn"
              >
                📋 Copy password
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setResetResult(null)}
                data-testid="reset-password-result-close-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DB MIGRATIONS — wipe + restore from a bundled snapshot */}
      {activeTab === 'migrations' && (
        <MigrationsPanel />
      )}

      {/* DEMO REQUESTS — inbound sales pipeline from /api/public/demo-request */}
      {activeTab === 'demo-requests' && (
        <DemoRequestsPanel />
      )}

      {/* REVENUE — MRR/ARR, cohort funnel, inactive orgs */}
      {activeTab === 'revenue' && (
        <RevenueTab />
      )}

      {/* AUDIT LOG — every super-admin action, filterable */}
      {activeTab === 'audit-log' && (
        <AuditLogTab />
      )}

      {/* EMAIL HEALTH — platform-wide SMTP deliverability */}
      {activeTab === 'email-health' && (
        <EmailHealthTab />
      )}
    </div>
  );
}

/**
 * Super-admin Database Migrations panel.
 *
 * Lists every snapshot bundled with the current build and offers a
 * type-to-confirm "Wipe + Restore" button. Used to seed a freshly-
 * deployed cluster from a snapshot exported in another environment.
 *
 * Safety rails enforced server-side:
 *   - super-admin auth
 *   - exact `WIPE_AND_RESTORE` confirm phrase
 *   - one-shot guard (must pass `force=true` to re-run)
 *   - super-admins preserved across the wipe so we don't lock out
 *     the operator running the restore
 */
function MigrationsPanel() {
  const [snapshots, setSnapshots] = React.useState([]);
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState('');
  const [mode, setMode] = React.useState('replace_target_org_only');
  const [confirmPhrase, setConfirmPhrase] = React.useState('');
  const [dryRun, setDryRun] = React.useState(true);
  const [force, setForce] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [snapsRes, statusRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/migrations/snapshots`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/migrations/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
      ]);
      if (snapsRes.ok) {
        const d = await snapsRes.json();
        setSnapshots(d.snapshots || []);
        if (!selectedSnapshot && (d.snapshots || []).length) {
          // Prefer a full-database snapshot by default — that's the
          // primary "seed a fresh prod cluster" path.
          const fullDb = (d.snapshots || []).find(s => s.scope === 'full_database');
          setSelectedSnapshot((fullDb || d.snapshots[0]).snapshot_id);
        }
      }
      if (statusRes.ok) {
        const d = await statusRes.json();
        setStatus(d.status || null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load migration data');
    } finally {
      setLoading(false);
    }
  }, [selectedSnapshot]);

  React.useEffect(() => { loadData(); }, [loadData]);

  const selectedMeta = snapshots.find(s => s.snapshot_id === selectedSnapshot);

  const runMigration = async () => {
    if (!selectedSnapshot) {
      setError('Pick a snapshot first.');
      return;
    }
    if (!dryRun && confirmPhrase !== 'WIPE_AND_RESTORE') {
      setError('Type WIPE_AND_RESTORE exactly to confirm.');
      return;
    }
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/migrations/restore-from-snapshot`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            snapshot_id: selectedSnapshot,
            // Dry run doesn't actually delete/insert, but the API still
            // expects the confirm phrase so we send it.
            confirm: 'WIPE_AND_RESTORE',
            // Full-database snapshots force a full wipe regardless of UI
            // choice (replace-target-only would leave stale rows).
            mode: selectedMeta?.scope === 'full_database' ? 'wipe_all_tenant_data' : mode,
            dry_run: dryRun,
            force,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setResult(data);
      // Re-fetch status so the "Last migration" banner reflects reality.
      loadData();
      // Reset confirm so a second click doesn't fire without re-typing.
      setConfirmPhrase('');
    } catch (err) {
      setError(err.message || 'Migration failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="migrations-panel">
      <div className="page-header">
        <h1>🗃️ Database Migrations</h1>
        <p>Wipe + restore the current database from a snapshot bundled with this build.</p>
      </div>

      {status?.last_migration_completed_at && (
        <div
          className="card"
          style={{ marginBottom: '1rem', borderLeft: '4px solid hsl(145 63% 42%)' }}
          data-testid="migration-status-banner"
        >
          <strong>✅ Previous migration on record</strong>
          <div style={{ fontSize: '0.875rem', marginTop: 4, color: 'hsl(var(--muted-foreground))' }}>
            {status.last_migration_snapshot_id} · completed{' '}
            {new Date(status.last_migration_completed_at).toLocaleString()}
          </div>
          <div style={{ fontSize: '0.8125rem', marginTop: 6 }}>
            ⚠️ Re-running requires ticking <strong>Force re-run</strong> below.
          </div>
        </div>
      )}

      {loading ? (
        <div className="card"><div className="loading">Loading…</div></div>
      ) : snapshots.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: '2.5rem' }}>📦</div>
            <h3>No snapshots bundled with this build</h3>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              Add a JSON file to <code>/app/backend/migrations/snapshots/</code> and redeploy.
            </p>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Bundled snapshots</h3>
          <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                <th style={{ padding: '0.5rem' }}>Snapshot</th>
                <th style={{ padding: '0.5rem' }}>Scope</th>
                <th style={{ padding: '0.5rem' }}>Org / Coverage</th>
                <th style={{ padding: '0.5rem' }}>Source</th>
                <th style={{ padding: '0.5rem' }}>Rows</th>
                <th style={{ padding: '0.5rem' }}>Exported at</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => {
                const scope = s.scope || 'single_org';
                const isFull = scope === 'full_database';
                const scopeBadge = isFull
                  ? { label: '🌐 Full database', bg: 'hsl(0 70% 92%)', fg: 'hsl(0 70% 30%)' }
                  : { label: '🏢 Single org', bg: 'hsl(210 80% 92%)', fg: 'hsl(210 80% 30%)' };
                const coverage = isFull
                  ? `${s.organization_count ?? '?'} orgs: ${(s.organization_slugs || []).join(', ') || '—'}`
                  : `${s.organization_name || '—'} (${s.organization_slug || '—'})`;
                return (
                  <tr
                    key={s.snapshot_id}
                    style={{
                      borderBottom: '1px solid hsl(var(--border))',
                      background: s.snapshot_id === selectedSnapshot ? 'hsl(var(--accent))' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedSnapshot(s.snapshot_id)}
                    data-testid={`migration-snapshot-row-${s.snapshot_id}`}
                  >
                    <td style={{ padding: '0.5rem' }}>
                      <strong>{s.snapshot_id}</strong>
                      {s.label && (
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                          {s.label}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.15rem 0.5rem',
                          borderRadius: 999,
                          background: scopeBadge.bg,
                          color: scopeBadge.fg,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                        data-testid={`migration-snapshot-scope-${s.snapshot_id}`}
                      >
                        {scopeBadge.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '0.8125rem' }}>{coverage}</td>
                    <td style={{ padding: '0.5rem' }}>{s.source_db || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{s.total_rows || 0}</td>
                    <td style={{ padding: '0.5rem' }}>
                      {s.exported_at ? new Date(s.exported_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedMeta && (
        <div className="card" style={{ borderLeft: '4px solid hsl(0 70% 50%)' }}>
          <h3 style={{ marginTop: 0 }}>
            Run migration on this database
          </h3>
          {(() => {
            const scope = selectedMeta.scope || 'single_org';
            const isFull = scope === 'full_database';
            if (isFull) {
              return (
                <div
                  style={{
                    padding: '0.6rem 0.8rem',
                    background: 'hsl(0 70% 96%)',
                    border: '1px solid hsl(0 70% 80%)',
                    borderRadius: 6,
                    marginBottom: '0.75rem',
                    fontSize: '0.85rem',
                  }}
                  data-testid="migration-full-db-warning"
                >
                  <strong>🌐 Full-database snapshot.</strong> This will wipe
                  EVERY tenant&apos;s data, global tables (system_state, billing
                  catalog) and GridFS blobs on this cluster, then load{' '}
                  <strong>{selectedMeta.organization_count ?? '?'}</strong>{' '}
                  orgs from the snapshot
                  {selectedMeta.organization_slugs?.length ? (
                    <> ({selectedMeta.organization_slugs.join(', ')})</>
                  ) : null}
                  . Use this when seeding a fresh cluster from preview.
                </div>
              );
            }
            return (
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                About to wipe <strong>this</strong> database (the one this
                build is connected to) and load{' '}
                <strong>{selectedMeta.snapshot_id}</strong> (
                {selectedMeta.total_rows} rows). Super-admin users will be
                preserved across the wipe.
              </p>
            );
          })()}

          <div className="form-group">
            <label>Mode</label>
            <select
              value={(selectedMeta.scope === 'full_database') ? 'wipe_all_tenant_data' : mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={selectedMeta.scope === 'full_database'}
              data-testid="migration-mode-select"
              style={{ maxWidth: 480 }}
            >
              {selectedMeta.scope !== 'full_database' && (
                <option value="replace_target_org_only">
                  Replace target org only ({selectedMeta.organization_slug}) — leaves other orgs untouched
                </option>
              )}
              <option value="wipe_all_tenant_data">
                Wipe ALL tenant data first {selectedMeta.scope === 'full_database' ? '(forced for full-database snapshots)' : '(every org-scoped doc) — recommended for fresh prod'}
              </option>
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="migration-dry-run"
              />
              <span>Dry run (no changes written — recommended first time)</span>
            </label>
          </div>

          {status?.last_migration_completed_at && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  data-testid="migration-force"
                />
                <span>Force re-run (a prior migration is already recorded)</span>
              </label>
            </div>
          )}

          {!dryRun && (
            <div className="form-group">
              <label>
                Type <code>WIPE_AND_RESTORE</code> to confirm
              </label>
              <input
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="WIPE_AND_RESTORE"
                data-testid="migration-confirm-phrase"
                style={{ maxWidth: 320 }}
              />
            </div>
          )}

          {error && (
            <div style={{ padding: '0.5rem 0.75rem', background: 'hsl(0 70% 95%)', color: 'hsl(0 70% 30%)', borderRadius: 6, marginBottom: '0.75rem' }}>
              {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={runMigration}
            disabled={
              running ||
              (!dryRun && confirmPhrase !== 'WIPE_AND_RESTORE')
            }
            data-testid="migration-run-btn"
          >
            {running
              ? (dryRun ? 'Running dry-run…' : 'Running migration…')
              : (dryRun ? '🧪 Run dry-run' : '🚨 Wipe and restore')}
          </button>
        </div>
      )}

      {result && (
        <div className="card" style={{ marginTop: '1rem', borderLeft: '4px solid hsl(145 63% 42%)' }} data-testid="migration-result-card">
          <h3 style={{ marginTop: 0 }}>
            {result.dry_run ? '🧪 Dry-run summary' : '✅ Migration complete'}
          </h3>
          <p style={{ fontSize: '0.875rem' }}>
            Snapshot: <strong>{result.snapshot?.snapshot_id}</strong> · mode:{' '}
            <strong>{result.mode}</strong>
          </p>
          <p style={{ fontSize: '0.875rem' }}>
            Deleted: <strong>{result.summary?.deleted_total}</strong> rows · Inserted:{' '}
            <strong>{result.summary?.inserted_total}</strong> rows
          </p>
          {result.preserved_super_admin_emails?.length > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
              Preserved super-admins: {result.preserved_super_admin_emails.join(', ')}
            </p>
          )}
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer' }}>Per-collection details</summary>
            <table style={{ width: '100%', fontSize: '0.8125rem', marginTop: 8, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'hsl(var(--muted))' }}>
                  <th style={{ padding: '0.35rem', textAlign: 'left' }}>Collection</th>
                  <th style={{ padding: '0.35rem', textAlign: 'right' }}>Deleted</th>
                  <th style={{ padding: '0.35rem', textAlign: 'right' }}>Inserted</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys({ ...result.deleted, ...result.inserted }).sort().map(coll => (
                  <tr key={coll}>
                    <td style={{ padding: '0.35rem' }}>{coll}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{result.deleted?.[coll] ?? 0}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{result.inserted?.[coll] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      {result && !result.dry_run && (
        <div
          className="card"
          style={{
            marginTop: '1rem',
            borderLeft: '4px solid hsl(38 92% 50%)',
            background: 'hsl(38 92% 96%)',
          }}
          data-testid="post-migration-password-prompt"
        >
          <h3 style={{ marginTop: 0, color: 'hsl(38 92% 30%)' }}>
            ⚠️ Next step — rotate the super-admin password
          </h3>
          <p style={{ fontSize: '0.875rem', margin: 0 }}>
            The restored DB carries whatever super-admin password was set
            in the snapshot&apos;s source environment (often a dev
            placeholder such as <code>super123</code>). Use the panel
            below to set a strong production password
            <strong> before announcing the app</strong>.
          </p>
        </div>
      )}

      <SuperAdminPasswordCard />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Super-admin password reset card
// ---------------------------------------------------------------------------
// Rendered at the bottom of the Migrations panel so the operator can
// rotate the inherited super-admin password immediately after a fresh
// production seed. Also surfaces duplicate super-admin rows (a known
// legacy artefact of the auto-bootstrap script) and lets the operator
// either:
//   • Rotate the password on every super-admin row in one click
//     (closes the "duplicate row as back-door" risk).
//   • Remove the duplicate rows entirely (refuses to delete the
//     caller&apos;s own row or the last remaining super-admin — safety rails).
// ---------------------------------------------------------------------------

function SuperAdminPasswordCard() {
  const [superAdmins, setSuperAdmins] = React.useState([]);
  const [duplicateEmails, setDuplicateEmails] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [scope, setScope] = React.useState('all'); // default: rotate all rows
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [removingId, setRemovingId] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/super-admins`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setSuperAdmins(data.super_admins || []);
      setDuplicateEmails(data.duplicate_emails || []);
    } catch (err) {
      setError(err.message || 'Failed to load super-admins');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Password strength heuristic — purely client-side hint.
  const strength = React.useMemo(() => {
    if (!newPassword) return { score: 0, label: '', color: 'hsl(0 0% 50%)' };
    let s = 0;
    if (newPassword.length >= 12) s += 1;
    if (newPassword.length >= 16) s += 1;
    if (/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword)) s += 1;
    if (/[0-9]/.test(newPassword)) s += 1;
    if (/[^A-Za-z0-9]/.test(newPassword)) s += 1;
    const labels = ['Too short', 'Weak', 'OK', 'Strong', 'Very strong', 'Excellent'];
    const colors = [
      'hsl(0 70% 50%)',
      'hsl(20 90% 55%)',
      'hsl(40 95% 50%)',
      'hsl(95 60% 45%)',
      'hsl(140 60% 40%)',
      'hsl(160 70% 40%)',
    ];
    return { score: s, label: labels[s], color: colors[s] };
  }, [newPassword]);

  const submit = async () => {
    setError('');
    setResult(null);
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setRunning(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/super-admins/reset-password`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ new_password: newPassword, scope }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
      setNewPassword('');
      setConfirmPassword('');
      load();
    } catch (err) {
      setError(err.message || 'Password reset failed');
    } finally {
      setRunning(false);
    }
  };

  const removeRow = async (userId, email) => {
    const confirmed = window.confirm(
      `Hard-delete the duplicate super-admin row?\n\n` +
      `_id: ${userId}\nemail: ${email}\n\n` +
      `This cannot be undone. The other super-admin rows will remain.`,
    );
    if (!confirmed) return;
    setRemovingId(userId);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/super-admins/${userId}/remove`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      load();
    } catch (err) {
      setError(err.message || 'Remove failed');
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div
      className="card"
      style={{ marginTop: '1rem' }}
      data-testid="super-admin-password-card"
    >
      <h3 style={{ marginTop: 0 }}>🔐 Super-admin password &amp; accounts</h3>
      <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
        Rotate the super-admin password (yours, or every super-admin row
        at once). The current password is <strong>not</strong> required —
        being authenticated as a super-admin is proof enough. Minimum 12
        characters.
      </p>

      {duplicateEmails.length > 0 && (
        <div
          style={{
            padding: '0.6rem 0.8rem',
            background: 'hsl(38 92% 96%)',
            border: '1px solid hsl(38 92% 80%)',
            borderRadius: 6,
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
          }}
          data-testid="super-admin-duplicate-warning"
        >
          <strong>⚠️ Duplicate super-admin rows detected.</strong>{' '}
          {duplicateEmails.join(', ')} appears on multiple rows. Each row
          has its own password hash, so a partial reset would leave the
          other rows as back-doors. Use <strong>scope: All super-admin
          rows</strong> below to rotate every row at once, or remove the
          duplicates with the trash icon.
        </div>
      )}

      {loading ? (
        <p style={{ fontSize: '0.875rem' }}>Loading…</p>
      ) : (
        <>
          <table
            style={{
              width: '100%',
              fontSize: '0.8125rem',
              marginBottom: '1rem',
              borderCollapse: 'collapse',
            }}
            data-testid="super-admin-list"
          >
            <thead>
              <tr style={{ background: 'hsl(var(--muted))', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem' }}>Email</th>
                <th style={{ padding: '0.4rem' }}>User id</th>
                <th style={{ padding: '0.4rem' }}>Created</th>
                <th style={{ padding: '0.4rem' }}>Last password change</th>
                <th style={{ padding: '0.4rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {superAdmins.map(sa => (
                <tr
                  key={sa._id}
                  style={{ borderBottom: '1px solid hsl(var(--border))' }}
                  data-testid={`super-admin-row-${sa._id}`}
                >
                  <td style={{ padding: '0.4rem' }}>
                    {sa.email}
                    {sa.is_current_user && (
                      <span
                        style={{
                          marginLeft: 6,
                          padding: '0.05rem 0.4rem',
                          borderRadius: 999,
                          background: 'hsl(210 80% 92%)',
                          color: 'hsl(210 80% 30%)',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      >
                        you
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.4rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {sa._id?.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    {sa.created_at ? new Date(sa.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    {sa.password_changed_at ? new Date(sa.password_changed_at).toLocaleString() : 'never'}
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    {sa.is_current_user ? (
                      <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                        (locked — cannot self-remove)
                      </span>
                    ) : (
                      <button
                        className="btn btn-sm"
                        onClick={() => removeRow(sa._id, sa.email)}
                        disabled={removingId === sa._id || superAdmins.length <= 1}
                        data-testid={`super-admin-remove-${sa._id}`}
                        style={{
                          background: 'hsl(0 70% 95%)',
                          color: 'hsl(0 70% 35%)',
                          border: '1px solid hsl(0 70% 80%)',
                        }}
                      >
                        {removingId === sa._id ? 'Removing…' : '🗑️ Remove'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="form-group">
            <label>Apply password change to</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="super-admin-password-scope"
              style={{ maxWidth: 480 }}
            >
              <option value="all">
                All super-admin rows ({superAdmins.length}) — recommended when duplicates exist
              </option>
              <option value="self">
                Only my account (the row currently signed in)
              </option>
            </select>
          </div>

          <div className="form-group">
            <label>New password (min 12 characters)</label>
            <div style={{ display: 'flex', gap: 8, maxWidth: 480 }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 12 characters, mix letters/digits/symbols"
                data-testid="super-admin-new-password"
                autoComplete="new-password"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setShowPassword(s => !s)}
                data-testid="super-admin-show-password-toggle"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {newPassword && (
              <div style={{ marginTop: 6, fontSize: '0.75rem' }} data-testid="super-admin-password-strength">
                Strength:{' '}
                <strong style={{ color: strength.color }}>{strength.label}</strong>
                <div
                  style={{
                    marginTop: 4,
                    height: 6,
                    background: 'hsl(var(--muted))',
                    borderRadius: 3,
                    maxWidth: 480,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${(strength.score / 5) * 100}%`,
                      height: '100%',
                      background: strength.color,
                      transition: 'width 200ms ease',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Confirm new password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type the same password again"
              data-testid="super-admin-confirm-password"
              autoComplete="new-password"
              style={{ maxWidth: 480 }}
            />
          </div>

          {error && (
            <div
              style={{
                padding: '0.5rem 0.75rem',
                background: 'hsl(0 70% 95%)',
                color: 'hsl(0 70% 30%)',
                borderRadius: 6,
                marginBottom: '0.75rem',
              }}
              data-testid="super-admin-password-error"
            >
              {error}
            </div>
          )}

          {result && (
            <div
              style={{
                padding: '0.6rem 0.8rem',
                background: 'hsl(145 63% 95%)',
                color: 'hsl(145 63% 25%)',
                borderRadius: 6,
                marginBottom: '0.75rem',
              }}
              data-testid="super-admin-password-result"
            >
              ✅ Password rotated on <strong>{result.modified_count}</strong>{' '}
              super-admin row(s). Affected:{' '}
              {result.affected_super_admins?.map(s => s.email).join(', ') || '—'}.
              <br />
              <span style={{ fontSize: '0.8125rem' }}>
                Your current session token is still valid; sign out and
                sign back in with the new password to verify.
              </span>
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={
              running ||
              !newPassword ||
              newPassword.length < 12 ||
              newPassword !== confirmPassword
            }
            data-testid="super-admin-password-submit"
          >
            {running ? 'Rotating password…' : '🔑 Rotate super-admin password'}
          </button>
        </>
      )}
    </div>
  );
}


/**
 * DemoRequestsPanel — inbound sales pipeline queue for super admins.
 *
 * Every "Book a 15-min demo" submission on the public landing/pricing
 * pages persists to `demo_requests` and shows up here in real time
 * (well, on tab open + explicit refresh). Rows can be moved through
 * a small pipeline: new → contacted → scheduled → won / lost / archived.
 *
 * Kept intentionally lightweight — this is a founder-run pipeline for
 * the first 20 customers, not a full CRM. Once volume warrants it,
 * export to HubSpot/Attio and delete this component.
 */
function DemoRequestsPanel() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('open');  // open | all | won | lost
  // Row currently open in the Provision modal. Null when closed.
  const [provisionRow, setProvisionRow] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getDemoRequests();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Failed to load demo requests: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (row, newStatus) => {
    setBusyId(row._id);
    try {
      await api.updateDemoRequestStatus(row._id, newStatus);
      await load();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Failed to update: ${err.message || err}`);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = React.useMemo(() => {
    if (statusFilter === 'all') return rows;
    if (statusFilter === 'open') return rows.filter((r) => !['won', 'lost', 'archived'].includes(r.status));
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const counts = React.useMemo(() => {
    const c = { new: 0, contacted: 0, scheduled: 0, won: 0, lost: 0, archived: 0, total: rows.length };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div data-testid="demo-requests-panel">
      <div className="page-header">
        <h1>📅 Demo Requests</h1>
        <p>
          Inbound sales enquiries from the public "Book a 15-min demo" CTA.
          Move each row through the pipeline as you speak with the nursery.
        </p>
      </div>

      {/* Pipeline summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        {[
          { key: 'new',       label: '🟠 New',        color: '#c66a1a' },
          { key: 'contacted', label: '💬 Contacted',  color: '#215d9a' },
          { key: 'scheduled', label: '🗓 Scheduled',   color: '#5b21b6' },
          { key: 'won',       label: '✅ Won',         color: '#0b7a5a' },
          { key: 'lost',      label: '⛔ Lost',        color: '#a12a2a' },
          { key: 'archived',  label: '📦 Archived',   color: '#4a5a68' },
        ].map((tile) => (
          <div key={tile.key} data-testid={`demo-tile-${tile.key}`} style={{
            background: 'white', border: '1px solid hsl(var(--border))', borderRadius: 10,
            padding: '0.6rem 0.8rem',
          }}>
            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', letterSpacing: '0.03em', textTransform: 'uppercase', fontWeight: 600 }}>{tile.label}</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: tile.color, marginTop: 2 }}>{counts[tile.key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', fontWeight: 700 }}>Show</span>
        {[
          { key: 'open', label: `Open (${counts.new + counts.contacted + counts.scheduled})` },
          { key: 'won',  label: `Won (${counts.won})` },
          { key: 'lost', label: `Lost (${counts.lost})` },
          { key: 'all',  label: `All (${counts.total})` },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setStatusFilter(opt.key)}
            data-testid={`demo-filter-${opt.key}`}
            style={{
              padding: '0.3rem 0.75rem', borderRadius: 999,
              border: `1px solid ${statusFilter === opt.key ? 'hsl(180 65% 35%)' : 'hsl(var(--border))'}`,
              background: statusFilter === opt.key ? 'hsl(180 65% 35%)' : 'white',
              color: statusFilter === opt.key ? 'white' : 'hsl(210 15% 25%)',
              fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          data-testid="demo-refresh"
          className="btn btn-ghost"
          style={{ marginLeft: 'auto', fontSize: '0.78rem' }}
        >
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }} data-testid="demo-loading">
          Loading demo requests…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', border: '1px dashed hsl(var(--border))', borderRadius: 12 }} data-testid="demo-empty">
          No demo requests in this view yet. Requests submitted via the public site will land here immediately.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {filtered.map((r) => (
            <div key={r._id} data-testid={`demo-row-${r._id}`} style={{
              background: 'white', border: '1px solid hsl(var(--border))', borderRadius: 12,
              padding: '0.85rem 1rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{r.name} <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>· {r.nursery || 'unspecified nursery'}</span></div>
                  <div style={{ marginTop: 2, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                    <a href={`mailto:${r.email}`} style={{ color: '#0b3d3a', fontWeight: 600 }}>{r.email}</a>
                    {r.phone && <> · <a href={`tel:${r.phone}`}>{r.phone}</a></>}
                    {r.size && <> · {r.size}</>}
                    {r.preferred_time && <> · Prefers {r.preferred_time}</>}
                  </div>
                  {r.message && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: 'hsl(210 30% 97%)', borderRadius: 8, fontSize: '0.85rem', color: 'hsl(210 15% 25%)', whiteSpace: 'pre-wrap' }}>
                      {r.message}
                    </div>
                  )}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                    Received {r.created_at ? new Date(r.created_at).toLocaleString('en-GB') : '—'}
                    {r.source && <> · via <code style={{ background: 'hsl(210 30% 95%)', padding: '0 4px', borderRadius: 3 }}>{r.source}</code></>}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-end', minWidth: 200 }}>
                  <span data-testid={`demo-status-${r._id}`} style={{
                    padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                    background: 'hsl(210 30% 95%)', color: 'hsl(210 15% 25%)', letterSpacing: '0.04em',
                  }}>
                    {r.status || 'new'}
                  </span>
                  <select
                    value={r.status || 'new'}
                    disabled={busyId === r._id}
                    onChange={(e) => setStatus(r, e.target.value)}
                    data-testid={`demo-status-select-${r._id}`}
                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: 6 }}
                  >
                    <option value="new">🟠 New</option>
                    <option value="contacted">💬 Contacted</option>
                    <option value="scheduled">🗓 Scheduled</option>
                    <option value="won">✅ Won</option>
                    <option value="lost">⛔ Lost</option>
                    <option value="archived">📦 Archived</option>
                  </select>
                  {/* Provisioning affordance — the whole point of the
                      "Won" state. If the row is already provisioned we
                      link straight to the branded welcome page instead
                      of offering to duplicate. */}
                  {r.provisioned_org_id ? (
                    <a
                      href={`/welcome/${r.provisioned_org_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`demo-open-org-${r._id}`}
                      style={{
                        fontSize: '0.78rem', fontWeight: 600, color: '#0b7a5a',
                        textDecoration: 'none', padding: '0.25rem 0.55rem',
                        border: '1px solid #0b7a5a', borderRadius: 999,
                        background: '#e6f7f0',
                      }}
                    >
                      🏫 Open workspace →
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setProvisionRow(r)}
                      disabled={busyId === r._id}
                      data-testid={`demo-provision-${r._id}`}
                      style={{
                        fontSize: '0.78rem', fontWeight: 700, color: 'white',
                        padding: '0.35rem 0.7rem', borderRadius: 999,
                        background: 'linear-gradient(120deg, #0b7a5a 0%, #159977 100%)',
                        border: 'none', cursor: 'pointer',
                        boxShadow: '0 1px 2px rgba(11, 122, 90, 0.35)',
                      }}
                    >
                      🚀 Provision nursery
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {provisionRow && (
        <ProvisionNurseryModal
          request={provisionRow}
          onClose={() => setProvisionRow(null)}
          onProvisioned={async () => { setProvisionRow(null); await load(); }}
        />
      )}
    </div>
  );
}


/**
 * ProvisionNurseryModal — two-stage handoff dialog.
 *
 *   Stage 1: confirm/tweak the details we'll seed the new workspace
 *            with (org name, slug, owner name/email). Cancels safely.
 *
 *   Stage 2: on success, show the new workspace URL + one-time
 *            temporary password with copy-to-clipboard. The owner
 *            is also emailed the same value in parallel.
 */
function ProvisionNurseryModal({ request, onClose, onProvisioned }) {
  const suggestedSlug = (request.nursery || request.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const [orgName, setOrgName] = React.useState(request.nursery || request.name || '');
  const [slug, setSlug] = React.useState(suggestedSlug);
  const [ownerName, setOwnerName] = React.useState(request.name || '');
  const [ownerEmail, setOwnerEmail] = React.useState(request.email || '');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.provisionDemoRequest(request._id, {
        organization_name: orgName,
        slug,
        owner_name: ownerName,
        owner_email: ownerEmail,
      });
      setResult(res);
    } catch (error) {
      setErr(error.message || 'Failed to provision');
    } finally {
      setBusy(false);
    }
  };

  const copy = (v) => {
    try { navigator.clipboard?.writeText(v); } catch { /* no-op */ }
  };

  return (
    <div
      onClick={onClose}
      data-testid="provision-modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6, 43, 41, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="provision-modal"
        role="dialog"
        aria-modal="true"
        style={{
          width: '100%', maxWidth: 560,
          background: 'white', borderRadius: 16,
          padding: '1.5rem',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 30px 60px -20px rgba(6, 43, 41, 0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: 'hsl(180 40% 40%)', textTransform: 'uppercase' }}>
              One-click handoff
            </div>
            <h2 style={{ margin: '0.15rem 0 0', fontSize: '1.35rem', fontWeight: 800, color: 'hsl(180 40% 15%)' }}>
              {result ? '🎉 Nursery provisioned' : '🚀 Provision new nursery workspace'}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="provision-modal-close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {result ? (
          <div data-testid="provision-modal-result">
            <p style={{ margin: '0 0 1rem', color: 'hsl(210 15% 30%)', fontSize: '0.95rem' }}>
              <strong>{result.organization_name}</strong> is live at{' '}
              <a href={result.welcome_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0b7a5a', fontWeight: 600 }}>
                {result.welcome_url}
              </a>
              . An email with the temp password was {result.email_sent ? '✅ sent' : '⚠️ NOT sent — share manually'}.
            </p>

            <div style={{ border: '1px solid hsl(var(--border))', borderRadius: 10, padding: '0.75rem 0.85rem', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', letterSpacing: '0.04em' }}>Owner login</div>
              <div style={{ marginTop: '0.35rem', display: 'grid', gap: '0.35rem', fontSize: '0.9rem' }}>
                <div><strong>Email:</strong> {result.owner_email} <button type="button" onClick={() => copy(result.owner_email)} style={copyBtnStyle}>copy</button></div>
                <div>
                  <strong>Temp password:</strong>{' '}
                  <code style={codeStyle} data-testid="provision-modal-temp-pw">{result.temporary_password}</code>{' '}
                  <button type="button" onClick={() => copy(result.temporary_password)} style={copyBtnStyle}>copy</button>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                  Owner will be forced to change this on their first login.
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <a
                href={result.welcome_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="provision-modal-open-welcome"
                className="btn btn-primary"
                style={{ textDecoration: 'none', flex: 1, textAlign: 'center' }}
              >
                Open branded welcome page →
              </a>
              <button type="button" onClick={() => onProvisioned && onProvisioned()} data-testid="provision-modal-done" className="btn btn-secondary" style={{ flex: 1 }}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'hsl(210 15% 40%)', lineHeight: 1.5 }}>
              We'll create a fresh multi-tenant workspace using the enquiry details.
              You can tweak anything below before we spin it up. The owner gets a
              temporary password emailed straight away.
            </p>
            {err && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fdeaea', border: '1px solid #d43a3a', borderRadius: 8, color: '#a12a2a', fontSize: '0.85rem' }}>
                {err}
              </div>
            )}
            <div style={{ display: 'grid', gap: '0.65rem' }}>
              <div>
                <label className="form-label">Organisation (nursery) name *</label>
                <input required value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="provision-org-name" />
              </div>
              <div>
                <label className="form-label">Public URL slug *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>/welcome/</span>
                  <input required value={slug} onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase())} data-testid="provision-slug" style={{ flex: 1 }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label className="form-label">Owner name *</label>
                  <input required value={ownerName} onChange={(e) => setOwnerName(e.target.value)} data-testid="provision-owner-name" />
                </div>
                <div>
                  <label className="form-label">Owner email *</label>
                  <input required type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} data-testid="provision-owner-email" />
                </div>
              </div>
            </div>
            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={onClose} disabled={busy} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              <button type="submit" disabled={busy} data-testid="provision-submit" className="btn btn-primary" style={{ flex: 2, fontWeight: 700 }}>
                {busy ? 'Provisioning…' : '🚀 Provision & email owner'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const copyBtnStyle = {
  marginLeft: '0.35rem', fontSize: '0.7rem', padding: '0.1rem 0.4rem',
  border: '1px solid hsl(var(--border))', borderRadius: 4, background: 'white', cursor: 'pointer',
};
const codeStyle = {
  background: 'hsl(210 30% 95%)', padding: '0.15rem 0.4rem', borderRadius: 4,
  fontFamily: 'monospace', fontSize: '0.88rem',
};


export default SuperAdminDashboard;
